import { Controller } from '@hotwired/stimulus';

// 住民税の月別テーブル (#2015 / #3117)。年税額の月割・列合計の再計算・
// 従業員ごとの逐次保存を行う。inline javascript: から移したもので、
// 計算ロジックは変更していない。
//
// 初期化は turbo:load から connect() へ移した。controller は #rtTable に付くので
// 旧コードの getElementById("rtTable") は this.element に相当する。
export default class extends Controller {
  connect() {
    const table = this.element;


    const MONTHS = [6, 7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5];
    const rows = Array.from(table.querySelectorAll("tbody tr"));
    const yen = (n) => "¥" + n.toLocaleString("en-US");
    const parseNum = (v) => {
      const n = parseInt((v || "").replace(/[^0-9]/g, ""), 10);
      return isNaN(n) ? 0 : n;
    };

    rows.forEach((row) => {
      const total = row.querySelector('input[id$="_m_total"]');
      row.dataset.uid = total ? total.id.replace(/^user_/, "").replace(/_m_total$/, "") : "";
    });

    const monthInput = (row, m) => document.getElementById(`user_${row.dataset.uid}_m_${m}`);

    // writeTotals=false の初期表示では空欄の合計を 0 で埋めない。
    // 未入力の従業員は合計を nil のまま送信し、サーバ側の「全員入力済み」判定を
    // 壊さないようにするため（既存挙動と一致）。
    const recalc = (writeTotals = false) => {
      const cols = MONTHS.map(() => 0);
      let grand = 0;
      let filled = 0;

      rows.forEach((row) => {
        let rowSum = 0;
        let hasVal = false;
        MONTHS.forEach((m, ci) => {
          const inp = monthInput(row, m);
          if (!inp) return;
          const v = parseNum(inp.value);
          if (inp.value.trim() !== "") hasVal = true;
          rowSum += v;
          cols[ci] += v;
        });
        grand += rowSum;
        if (hasVal) filled += 1;

        // 合計欄が readonly でも JS が算出した合計は反映する。readonly は
        // ユーザーの直接入力を防ぐだけで、月割の自動計算結果の表示は妨げない。
        // readonly な input もフォーム送信時に値が送られるため保存も正しい (#2170)。
        const totalInput = row.querySelector('input[id$="_m_total"]');
        if (writeTotals && hasVal && totalInput) totalInput.value = rowSum;
      });

      cols.forEach((c, ci) => {
        const cell = document.getElementById(`rt-col-${ci}`);
        if (cell) cell.innerHTML = '<span class="ft-yen">¥</span>' + c.toLocaleString("en-US");
      });
      const grandCell = document.getElementById("rt-grand-cell");
      if (grandCell) grandCell.innerHTML = '<span class="yen">¥</span>' + grand.toLocaleString("en-US");
      const grandTotal = document.getElementById("rt-grand-total");
      if (grandTotal) grandTotal.textContent = yen(grand);
      const filledCount = document.getElementById("rt-filled-count");
      if (filledCount) filledCount.textContent = filled;
    };

    table.querySelectorAll(".rt-month-input").forEach((inp) => {
      inp.addEventListener("input", () => recalc(true));
    });

    // 年税額を 12 で割り 100 円未満を切り捨てた均等額を 7月〜翌5月に充て、
    // 端数の合計を 6月にまとめる（既存の月割ロジックと同一）。少額でも一括徴収にせず必ず月割する。
    const splitIntoMonths = (row, annual) => {
      const base = Math.floor(annual / 12 / 100) * 100;
      const first = annual - base * 11;
      MONTHS.forEach((m) => {
        const inp = monthInput(row, m);
        if (!inp || inp.readOnly) return;
        inp.value = m === 6 ? first : base;
      });
    };

    // 合計（年税額）に入力したら各月へ自動で月割りする。
    rows.forEach((row) => {
      const totalInput = row.querySelector('input[id$="_m_total"]');
      if (!totalInput || totalInput.readOnly) return;
      totalInput.addEventListener("input", () => {
        const annual = parseNum(totalInput.value);
        splitIntoMonths(row, annual);
        recalc(true);
        totalInput.value = annual;
      });
    });

    recalc(false);

    // 保存結果を描いたら URL から saved を落とす。残しておくと、その後の
    // 再読込や戻る操作で、保存していないのに「保存しました」が再表示される。
    if (new URL(window.location.href).searchParams.has("saved")) {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("saved");
      window.history.replaceState({}, "", clean.pathname + clean.search);
    }

    const toggle = document.getElementById("rtDetailToggle");
    const body = document.getElementById("rtDetailBody");
    const label = document.getElementById("rtDetailLabel");
    if (toggle && body && label) {
      toggle.addEventListener("click", () => {
        const open = toggle.classList.toggle("open");
        body.classList.toggle("open", open);
        label.textContent = open ? "詳細な説明を閉じる" : "詳細な説明を見る";
      });
    }

    // 保存: 従業員 (行) ごとに 1 リクエストずつ PUT する (#2015)。
    // 全従業員を 1 フォームで送ると 1 従業員 13 入力 × 従業員数 が
    // Rack のパラメータ数上限 (PARAMS_LIMIT=4096) を超え 400 になるため、
    // 各リクエストを当該行の約 13 パラメータだけに保つ。
    const form = document.getElementById("rtForm");
    const saveBtn = document.getElementById("rtSaveBtn");
    const statusBox = document.getElementById("rtSaveStatus");
    const statusMsg = document.getElementById("rtSaveMsg");
    const putUrl = form ? form.dataset.putUrl : null;
    const csrfToken = () => {
      const meta = document.querySelector('meta[name="csrf-token"]');
      return meta ? meta.getAttribute("content") : "";
    };

    const showStatus = (text, state) => {
      if (!statusBox || !statusMsg) return;
      statusBox.hidden = false;
      statusBox.dataset.state = state || "";
      statusMsg.textContent = text;
    };

    // 1 行分の payload (rtax[uid][total,m1..m12]) を組み立てる。
    const rowFields = (row) => {
      const uid = row.dataset.uid;
      const fields = {};
      const totalInput = row.querySelector('input[id$="_m_total"]');
      if (totalInput) fields[`rtax[${uid}][total]`] = totalInput.value;
      MONTHS.forEach((m) => {
        const inp = monthInput(row, m);
        if (inp) fields[`rtax[${uid}][m${m}]`] = inp.value;
      });
      return fields;
    };

    const putRow = async (row) => {
      const params = new URLSearchParams();
      const fields = rowFields(row);
      Object.keys(fields).forEach((k) => params.append(k, fields[k]));
      const res = await fetch(putUrl, {
        method: "PUT",
        headers: {
          "X-CSRF-Token": csrfToken(),
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: params.toString(),
      });
      return res.ok;
    };

    // 小さめの並列度 (CONCURRENCY) で逐次的に送り、サーバ/接続を飽和させない。
    const CONCURRENCY = 5;
    const saveAllRows = async () => {
      const queue = rows.slice();
      const failed = [];
      let done = 0;
      const total = queue.length;

      const worker = async () => {
        while (queue.length) {
          const row = queue.shift();
          row.classList.remove("rt-row-error");
          let ok = false;
          try {
            ok = await putRow(row);
          } catch (err) {
            ok = false;
          }
          if (!ok) {
            failed.push(row);
            row.classList.add("rt-row-error");
          }
          done += 1;
          showStatus(`保存中… ${done} / ${total}名`, "saving");
        }
      };

      const workers = [];
      for (let i = 0; i < Math.min(CONCURRENCY, total); i += 1) workers.push(worker());
      await Promise.all(workers);
      return { failed, total };
    };

    if (form && saveBtn && putUrl) {
      form.addEventListener("submit", (e) => {
        e.preventDefault();
      });
      saveBtn.addEventListener("click", async (e) => {
        e.preventDefault();
        if (saveBtn.disabled) return;
        saveBtn.disabled = true;
        recalc(true);
        showStatus("保存中…", "saving");
        const { failed, total } = await saveAllRows();
        saveBtn.disabled = false;
        if (failed.length === 0) {
          // 保存結果はサーバ側で描き直す。ここでメッセージを出してから
          // 再読込すると、表示した瞬間に消えて読めない。
          const url = new URL(window.location.href);
          url.searchParams.set("saved", "1");
          window.location.assign(url.pathname + url.search);
        } else {
          showStatus(
            `${total - failed.length}名を保存しました。${failed.length}名の保存に失敗しました。赤色の行の金額をご確認ください。`,
            "error"
          );
        }
      });
    }
  }
}
